'use client'

import { Suspense, useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import { useAuth } from '../contexts/AuthContext'
import { useRouter, usePathname } from 'next/navigation'
import { useVisibilityAwarePolling } from '@/lib/use-visibility-aware-polling'
import { authenticatedFetch } from '@/lib/client-fetch'

interface NavbarProps {
  activeTab?: 'rider' | 'driver' | 'admin' | 'student'
}

function NavbarContent({ activeTab }: NavbarProps) {
  const { user, logout } = useAuth()
  const router = useRouter()
  const pathname = usePathname()
  const [hasStripeAccount, setHasStripeAccount] = useState<boolean | null>(null)
  const [checkingStripe, setCheckingStripe] = useState(false)
  const [hasEnRouteOrders, setHasEnRouteOrders] = useState(false)
  const [unreadChatCount, setUnreadChatCount] = useState(0)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const checkStripeAccount = useCallback(async () => {
    if (!user?.id) return

    // A connected account doesn't disconnect mid-session: once we've seen
    // payouts enabled, remember it for the browser session and stop hitting
    // /api/stripe/connect (a Prisma lookup + possible live Stripe call).
    try {
      if (sessionStorage.getItem(`deeliver:stripe-ok:${user.id}`) === '1') {
        setHasStripeAccount(true)
        return
      }
    } catch {}

    setCheckingStripe(true)
    try {
      const response = await authenticatedFetch(`/api/stripe/connect?userId=${user.id}`)
      if (response.ok) {
        const data = await response.json()
        const connected = data.connected && data.payoutsEnabled
        setHasStripeAccount(connected)
        if (connected) {
          try { sessionStorage.setItem(`deeliver:stripe-ok:${user.id}`, '1') } catch {}
        }
      } else {
        setHasStripeAccount(false)
      }
    } catch (error) {
      console.error('Error checking Stripe account:', error)
      setHasStripeAccount(false)
    } finally {
      setCheckingStripe(false)
    }
  }, [user?.id])

  const fetchDriverUnreadCount = useCallback(async () => {
    if (!user?.id || user.type !== 'driver') return

    try {
      const response = await authenticatedFetch(`/api/chat/unread-count?driverId=${user.id}`)
      if (response.ok) {
        const data = await response.json()
        setUnreadChatCount(data.unreadCount || 0)
      }
    } catch (error) {
      console.error('Error fetching unread chat count:', error)
    }
  }, [user?.id, user?.type])

  useEffect(() => {
    if (user && user.type === 'driver') {
      checkStripeAccount()
      fetchDriverUnreadCount()
    } else {
      setHasStripeAccount(null)
      setUnreadChatCount(0)
    }
  }, [user, checkStripeAccount, fetchDriverUnreadCount])

  // Visibility-aware: pauses while tab is hidden so the driver's overnight
  // open tab doesn't keep Neon warm.
  useVisibilityAwarePolling(
    fetchDriverUnreadCount,
    30000,
    !!user && user.type === 'driver'
  )

  // Re-check when returning from settings page (in case they just connected).
  // Only while the account is known-unconnected — a connected driver was
  // previously re-checked (DB + Stripe API) on every single navigation.
  useEffect(() => {
    if (user && user.type === 'driver' && hasStripeAccount === false && pathname && !pathname.includes('/driver/settings')) {
      // Small delay to allow Stripe webhook to process if they just connected
      const timer = setTimeout(() => {
        checkStripeAccount()
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [pathname, user, hasStripeAccount, checkStripeAccount])

  // Cheap boolean endpoint; replaces a full /api/orders fetch (with joins)
  // that was running every 15s just to drive a single navbar badge.
  const checkEnRouteOrders = useCallback(async () => {
    if (!user?.id) return

    try {
      const response = await authenticatedFetch('/api/orders/has-en-route')
      if (response.ok) {
        const data = await response.json()
        setHasEnRouteOrders(!!data.hasEnRoute)
      }
    } catch (error) {
      console.error('Error checking en_route orders:', error)
    }
  }, [user?.id])

  useEffect(() => {
    if (user && (user.type === 'student' || user.type === 'parent')) {
      checkEnRouteOrders()
    } else {
      setHasEnRouteOrders(false)
    }
  }, [user, checkEnRouteOrders])

  useVisibilityAwarePolling(
    checkEnRouteOrders,
    30000,
    !!user && (user.type === 'student' || user.type === 'parent')
  )

  const handleLogout = () => {
    logout()
  }

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (mobileMenuOpen && !target.closest('nav')) {
        setMobileMenuOpen(false)
      }
    }

    if (mobileMenuOpen) {
      document.addEventListener('click', handleClickOutside)
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.removeEventListener('click', handleClickOutside)
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen])

  return (
    <>
      {/* Warning banner for drivers without connected bank account */}
      {user?.type === 'driver' && hasStripeAccount === false && !checkingStripe && (
        <div className="bg-yellow-50 border-b border-yellow-300 px-4 sm:px-8 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-start">
              <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="font-medium text-yellow-800 mb-1">Bank Account Not Connected</p>
                <p className="text-sm text-yellow-700">
                  You will not receive payments until you connect your bank account. 
                  <Link href="/driver/settings" className="underline font-medium hover:text-yellow-900 ml-1">
                    Connect your account now →
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    <nav className="sticky top-0 z-50 flex items-center justify-between px-4 sm:px-6 md:px-8 py-3 sm:py-4 bg-cream/85 backdrop-blur-md border-b border-ink/[0.06]">
      <Link href="/" className="font-serif italic font-semibold text-xl sm:text-2xl text-ink tracking-tight flex items-center gap-1 sm:gap-2 relative">
        {user?.type === 'driver' && unreadChatCount > 0 && (
          <span className="absolute -top-1 -right-3 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500 rounded-full border-2 border-white"></span>
        )}
        <span>deeliver<span className="text-ember">.</span></span>
      </Link>
      
      {/* Desktop Navigation */}
      <div className="hidden md:flex items-center gap-4 lg:gap-8">
        {user ? (
          <>
            {user.type === 'parent' ? (
              <>
                <Link 
                  href="/rider/new-order" 
                  className={`font-medium transition text-xs sm:text-sm whitespace-nowrap flex items-center min-h-[44px] ${
                    pathname === '/rider/new-order' || pathname.startsWith('/rider/new-order')
                      ? 'text-gray-900 border-b-2 border-brand-primary pb-1' 
                      : 'text-gray-700 hover:text-gray-900 hover:border-b-2 hover:border-gray-300 pb-1'
                  }`}
                >
                  New Order
                </Link>
                <Link 
                  href="/rider/orders" 
                  className={`font-medium transition text-xs sm:text-sm whitespace-nowrap flex items-center min-h-[44px] ${
                    pathname === '/rider/orders' || pathname.startsWith('/rider/orders')
                      ? 'text-gray-900 border-b-2 border-brand-primary pb-1' 
                      : 'text-gray-700 hover:text-gray-900 hover:border-b-2 hover:border-gray-300 pb-1'
                  }`}
                >
                  Orders
                </Link>
                {hasEnRouteOrders && (
                  <Link 
                    href="/track-order" 
                    className={`font-medium transition text-xs sm:text-sm whitespace-nowrap flex items-center min-h-[44px] ${
                      pathname === '/track-order' || pathname.startsWith('/track-order')
                        ? 'text-gray-900 border-b-2 border-brand-primary pb-1' 
                        : 'text-gray-700 hover:text-gray-900 hover:border-b-2 hover:border-gray-300 pb-1'
                    }`}
                  >
                    📍 Track
                  </Link>
                )}
                <Link 
                  href="/parent/settings" 
                  className={`font-medium transition text-xs sm:text-sm whitespace-nowrap flex items-center min-h-[44px] ${
                    pathname === '/parent/settings' || pathname.startsWith('/parent/settings')
                      ? 'text-gray-900 border-b-2 border-brand-primary pb-1' 
                      : 'text-gray-700 hover:text-gray-900 hover:border-b-2 hover:border-gray-300 pb-1'
                  }`}
                >
                  Settings
                </Link>
              </>
            ) : null}
            {user.type === 'student' ? (
              <>
                <Link 
                  href="/student/orders" 
                  className={`font-medium transition text-xs sm:text-sm whitespace-nowrap flex items-center min-h-[44px] ${
                    pathname === '/student/orders' || pathname.startsWith('/student/orders')
                      ? 'text-gray-900 border-b-2 border-brand-primary pb-1' 
                      : 'text-gray-700 hover:text-gray-900 hover:border-b-2 hover:border-gray-300 pb-1'
                  }`}
                >
                  Orders
                </Link>
                {hasEnRouteOrders && (
                  <Link 
                    href="/track-order" 
                    className={`font-medium transition text-xs sm:text-sm whitespace-nowrap flex items-center min-h-[44px] ${
                      pathname === '/track-order' || pathname.startsWith('/track-order')
                        ? 'text-gray-900 border-b-2 border-brand-primary pb-1' 
                        : 'text-gray-700 hover:text-gray-900 hover:border-b-2 hover:border-gray-300 pb-1'
                    }`}
                  >
                    📍 Track
                  </Link>
                )}
                <Link 
                  href="/student/confirm" 
                  className={`font-medium transition text-xs sm:text-sm whitespace-nowrap flex items-center min-h-[44px] ${
                    pathname === '/student/confirm' || pathname.startsWith('/student/confirm')
                      ? 'text-gray-900 border-b-2 border-brand-primary pb-1' 
                      : 'text-gray-700 hover:text-gray-900 hover:border-b-2 hover:border-gray-300 pb-1'
                  }`}
                >
                  Confirm
                </Link>
              </>
            ) : null}
            {user.type === 'driver' ? (
              <>
                <Link 
                  href="/driver/schedule" 
                  className={`font-medium transition text-xs sm:text-sm whitespace-nowrap flex items-center min-h-[44px] ${
                    pathname === '/driver/schedule' || pathname.startsWith('/driver/schedule')
                      ? 'text-gray-900 border-b-2 border-brand-primary pb-1' 
                      : 'text-gray-700 hover:text-gray-900 hover:border-b-2 hover:border-gray-300 pb-1'
                  }`}
                >
                  Schedule
                </Link>
                <Link 
                  href="/driver/orders" 
                  className={`font-medium transition text-xs sm:text-sm relative whitespace-nowrap flex items-center min-h-[44px] ${
                    pathname === '/driver/orders' || pathname.startsWith('/driver/orders')
                      ? 'text-gray-900 border-b-2 border-brand-primary pb-1' 
                      : 'text-gray-700 hover:text-gray-900 hover:border-b-2 hover:border-gray-300 pb-1'
                  }`}
                >
                  Orders{unreadChatCount > 0 && ` (${unreadChatCount})`}
                </Link>
                <Link 
                  href="/driver/routes" 
                  className={`font-medium transition text-xs sm:text-sm whitespace-nowrap flex items-center min-h-[44px] ${
                    pathname === '/driver/routes' || pathname.startsWith('/driver/routes')
                      ? 'text-gray-900 border-b-2 border-brand-primary pb-1' 
                      : 'text-gray-700 hover:text-gray-900 hover:border-b-2 hover:border-gray-300 pb-1'
                  }`}
                >
                  Routes
                </Link>
                <Link 
                  href="/driver/settings" 
                  className={`font-medium transition text-xs sm:text-sm whitespace-nowrap flex items-center min-h-[44px] ${
                    pathname === '/driver/settings' || pathname.startsWith('/driver/settings')
                      ? 'text-gray-900 border-b-2 border-brand-primary pb-1' 
                      : 'text-gray-700 hover:text-gray-900 hover:border-b-2 hover:border-gray-300 pb-1'
                  }`}
                >
                  Settings
                </Link>
              </>
            ) : null}
            {/* Admin tab removed */}
            <div className="flex items-center flex-wrap gap-2 sm:gap-4">
              <span className="text-gray-700 text-xs sm:text-sm break-words max-w-[120px] sm:max-w-none flex items-center min-h-[44px]">
                {user.name} ({user.type})
              </span>
              <button
                onClick={handleLogout}
                className="text-gray-700 font-medium hover:text-gray-900 transition text-xs sm:text-sm whitespace-nowrap flex items-center min-h-[44px]"
              >
                Logout
              </button>
            </div>
          </>
        ) : (
          <>
            <Link 
              href="/rider" 
              className={`font-medium transition text-xs sm:text-sm whitespace-nowrap flex items-center min-h-[44px] ${
                activeTab === 'rider'
                  ? 'text-gray-900 border-b-2 border-brand-primary pb-1'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              Ride
            </Link>
            <Link 
              href="/driver" 
              className={`font-medium transition text-xs sm:text-sm whitespace-nowrap flex items-center min-h-[44px] ${
                activeTab === 'driver'
                  ? 'text-gray-900 border-b-2 border-brand-primary pb-1'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              Drive
            </Link>
            <Link href="/register" className="text-gray-700 font-medium hover:text-gray-900 hover:border-b-2 hover:border-gray-300 pb-1 transition text-xs sm:text-sm whitespace-nowrap flex items-center min-h-[44px]">
              Register
            </Link>
            <Link href="/login" className="text-gray-700 font-medium hover:text-gray-900 hover:border-b-2 hover:border-gray-300 pb-1 transition text-xs sm:text-sm whitespace-nowrap flex items-center min-h-[44px]">
              Login
            </Link>
            {/* Admin tab removed */}
          </>
        )}
        {/* Help tab removed */}
      </div>

      {/* Mobile Menu Button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          setMobileMenuOpen(!mobileMenuOpen)
        }}
        className="md:hidden p-2 text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-primary rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center z-50 relative"
        aria-label="Toggle menu"
        aria-expanded={mobileMenuOpen}
        type="button"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          {mobileMenuOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/20 z-[99] md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Menu */}
          <div className="absolute md:hidden top-full left-0 right-0 bg-cream border-b border-ink/10 shadow-xl z-[100] max-h-[calc(100vh-200px)] overflow-y-auto">
          <div className="px-4 py-4 space-y-3">
            {user ? (
              <>
                {user.type === 'parent' && (
                  <>
                    <Link 
                      href="/rider/new-order" 
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block py-2 px-3 rounded-lg font-medium transition ${
                        pathname === '/rider/new-order' || pathname.startsWith('/rider/new-order')
                          ? 'text-gray-900 bg-brand-light' 
                          : 'text-gray-700 hover:bg-cream-deep/60'
                      }`}
                    >
                      New Order
                    </Link>
                    <Link 
                      href="/rider/orders" 
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block py-2 px-3 rounded-lg font-medium transition ${
                        pathname === '/rider/orders' || pathname.startsWith('/rider/orders')
                          ? 'text-gray-900 bg-brand-light' 
                          : 'text-gray-700 hover:bg-cream-deep/60'
                      }`}
                    >
                      Orders
                    </Link>
                    {hasEnRouteOrders && (
                      <Link 
                        href="/track-order" 
                        onClick={() => setMobileMenuOpen(false)}
                        className={`block py-2 px-3 rounded-lg font-medium transition ${
                          pathname === '/track-order' || pathname.startsWith('/track-order')
                            ? 'text-gray-900 bg-brand-light' 
                            : 'text-gray-700 hover:bg-cream-deep/60'
                        }`}
                      >
                        📍 Track
                      </Link>
                    )}
                    <Link 
                      href="/parent/settings" 
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block py-2 px-3 rounded-lg font-medium transition ${
                        pathname === '/parent/settings' || pathname.startsWith('/parent/settings')
                          ? 'text-gray-900 bg-brand-light' 
                          : 'text-gray-700 hover:bg-cream-deep/60'
                      }`}
                    >
                      Settings
                    </Link>
                  </>
                )}
                {user.type === 'student' && (
                  <>
                    <Link 
                      href="/student/orders" 
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block py-2 px-3 rounded-lg font-medium transition ${
                        pathname === '/student/orders' || pathname.startsWith('/student/orders')
                          ? 'text-gray-900 bg-brand-light' 
                          : 'text-gray-700 hover:bg-cream-deep/60'
                      }`}
                    >
                      Orders
                    </Link>
                    {hasEnRouteOrders && (
                      <Link 
                        href="/track-order" 
                        onClick={() => setMobileMenuOpen(false)}
                        className={`block py-2 px-3 rounded-lg font-medium transition ${
                          pathname === '/track-order' || pathname.startsWith('/track-order')
                            ? 'text-gray-900 bg-brand-light' 
                            : 'text-gray-700 hover:bg-cream-deep/60'
                        }`}
                      >
                        📍 Track
                      </Link>
                    )}
                    <Link 
                      href="/student/confirm" 
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block py-2 px-3 rounded-lg font-medium transition ${
                        pathname === '/student/confirm' || pathname.startsWith('/student/confirm')
                          ? 'text-gray-900 bg-brand-light' 
                          : 'text-gray-700 hover:bg-cream-deep/60'
                      }`}
                    >
                      Confirm
                    </Link>
                  </>
                )}
                {user.type === 'driver' && (
                  <>
                    <Link 
                      href="/driver/schedule" 
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block py-2 px-3 rounded-lg font-medium transition ${
                        pathname === '/driver/schedule' || pathname.startsWith('/driver/schedule')
                          ? 'text-gray-900 bg-brand-light' 
                          : 'text-gray-700 hover:bg-cream-deep/60'
                      }`}
                    >
                      Schedule
                    </Link>
                    <Link 
                      href="/driver/orders" 
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block py-2 px-3 rounded-lg font-medium transition relative ${
                        pathname === '/driver/orders' || pathname.startsWith('/driver/orders')
                          ? 'text-gray-900 bg-brand-light' 
                          : 'text-gray-700 hover:bg-cream-deep/60'
                      }`}
                    >
                      Orders{unreadChatCount > 0 && ` (${unreadChatCount})`}
                    </Link>
                    <Link 
                      href="/driver/routes" 
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block py-2 px-3 rounded-lg font-medium transition ${
                        pathname === '/driver/routes' || pathname.startsWith('/driver/routes')
                          ? 'text-gray-900 bg-brand-light' 
                          : 'text-gray-700 hover:bg-cream-deep/60'
                      }`}
                    >
                      Routes
                    </Link>
                    <Link 
                      href="/driver/settings" 
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block py-2 px-3 rounded-lg font-medium transition ${
                        pathname === '/driver/settings' || pathname.startsWith('/driver/settings')
                          ? 'text-gray-900 bg-brand-light' 
                          : 'text-gray-700 hover:bg-cream-deep/60'
                      }`}
                    >
                      Settings
                    </Link>
                  </>
                )}
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="px-3 py-2 text-sm text-gray-700">
                    {user.name} ({user.type})
                  </div>
                  <button
                    onClick={() => {
                      handleLogout()
                      setMobileMenuOpen(false)
                    }}
                    className="w-full text-left py-2 px-3 rounded-lg font-medium text-gray-700 hover:bg-cream-deep/60 transition"
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link 
                  href="/rider" 
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-2 px-3 rounded-lg font-medium transition ${
                    activeTab === 'rider' 
                      ? 'text-gray-900 bg-brand-light' 
                      : 'text-gray-700 hover:bg-cream-deep/60'
                  }`}
                >
                  Ride
                </Link>
                <Link 
                  href="/driver" 
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-2 px-3 rounded-lg font-medium transition ${
                    activeTab === 'driver' 
                      ? 'text-gray-900 bg-brand-light' 
                      : 'text-gray-700 hover:bg-cream-deep/60'
                  }`}
                >
                  Drive
                </Link>
                <Link 
                  href="/register" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 px-3 rounded-lg font-medium text-gray-700 hover:bg-cream-deep/60 transition"
                >
                  Register
                </Link>
                <Link 
                  href="/login" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 px-3 rounded-lg font-medium text-gray-700 hover:bg-cream-deep/60 transition"
                >
                  Login
                </Link>
              </>
            )}
          </div>
          </div>
        </>
      )}
    </nav>
    </>
  )
}

export default function Navbar({ activeTab }: NavbarProps) {
  // The Capacitor app shell (app/layout.tsx) provides BottomNav instead of the web navbar.
  if (process.env.NEXT_PUBLIC_MOBILE_UI_ONLY === 'true') return null
  return (
    <Suspense fallback={
      <nav className="flex items-center justify-between px-8 py-4 bg-cream/85 backdrop-blur-md border-b border-ink/[0.06]">
        <Link href="/" className="font-serif italic font-semibold text-2xl text-ink tracking-tight flex items-center gap-2">
          <span>deeliver<span className="text-ember">.</span></span>
        </Link>
        <div className="text-gray-600 text-sm">Loading...</div>
      </nav>
    }>
      <NavbarContent activeTab={activeTab} />
    </Suspense>
  )
}
